<template>
  <div class="flex flex-column gap-4" v-if="list">
    <a-table
      size="small"
      :pagination="false"
      :columns="columns"
      :data-source="list"
      :scroll="{ y: 230, x: 1200 }"
      :locale="{ emptyText: 'Actions Not Found' }"
    >
      <span slot="photo" slot-scope="text, record">
        <user-avatar :width="48" :height="48" :image-path="record.photo_url" />
      </span>
      <span slot="device" slot-scope="text, record">
        {{ formatDeviceType(record) }}
      </span>
      <span slot="date" slot-scope="text, record">
        {{ formatDateTime(record) }}
      </span>
      <span slot="action" slot-scope="text, record">
        {{ getActionLabel(record) }}
      </span>
      <span slot="address" slot-scope="text, record" class="flex gap-2 align-items-center">
        {{ record.address }}
        <button-more :record="record" :menu-items="menuItems">
          <template #trigger="{}">
            <div class="action-button" @click.stop="() => {}">
              <a-icon width="16" height="16" icon="fluent:globe-location-20-filled" />
            </div> </template
        ></button-more>
      </span>
      <span slot="in-wa" slot-scope="text, record">
        <a-tag :color="getTagColor(record)">{{ record.in_wa ? 'YES' : 'NO' }}</a-tag>
      </span>
      <span slot="wa-name" slot-scope="text, record">
        {{ getWorkAreaName(record) }}
      </span>
    </a-table>
    <span> <b>*</b> {{ outsideWorkAreaNote }} </span>
  </div>
</template>

<script>
import { Table, Tag } from 'ant-design-vue'
import Util from '@/util'
import UserAvatar from './ui/user-avatar.vue'

const statusTagColors = {
  GREEN: '#10b981',
  RED: '#ef4444'
}

const shiftActionEnum = Object.freeze({
  work_start: 'Work Start',
  work_end: 'Work End',
  lunch_start: '1st Meal Break Start',
  lunch_end: '1st Meal Break End',
  second_lunch_start: '2nd Meal Break Start',
  second_lunch_end: '2nd Meal Break End'
})

export default {
  components: {
    'a-table': Table,
    'a-tag': Tag,
    'user-avatar': UserAvatar
  },
  props: ['list'],
  data() {
    return {
      columns: [
        {
          title: 'Photo',
          scopedSlots: { customRender: 'photo' },
          key: 'photo',
          width: '60px'
        },
        {
          title: 'Date and Time',
          scopedSlots: { customRender: 'date' },
          key: 'date',
          width: '160px',
          sorter: (a, b) => new Date(a.date) - new Date(b.date)
        },
        {
          title: 'Device',
          key: 'device',
          width: '100px',
          scopedSlots: { customRender: 'device' },
          sorter: (a, b) => a.device_type.localeCompare(b.device_type)
        },
        {
          title: 'Action',
          scopedSlots: { customRender: 'action' },
          key: 'action',
          width: '100px',
          sorter: (a, b) => {
            const actionA = a.action ? a.action : ''
            const actionB = b.action ? b.action : ''
            return actionA.localeCompare(actionB)
          }
        },
        {
          title: 'In WA',
          scopedSlots: { customRender: 'in-wa' },
          key: 'inWa',
          width: '80px',
          sorter: (a, b) => (a.in_wa ? 1 : 0) - (b.in_wa ? 1 : 0)
        },
        {
          title: 'Distance to WA',
          dataIndex: 'distance',
          width: '150px',
          customRender: (_, record) => {
            return `${((record?.distance ?? 0) / 0.9144).toFixed(0)} yd`
          },
          sorter: (a, b) => a?.distance - b?.distance
        },
        {
          title: 'Address',
          dataIndex: 'address',
          width: '300px',
          scopedSlots: { customRender: 'address' },
          sorter: (a, b) => a.address.localeCompare(b.address)
        },
        {
          title: 'Geolocation Spread',
          dataIndex: 'radius',
          width: '170px',
          customRender: (_, record) => {
            return `${((record?.radius ?? 0) / 0.9144).toFixed(0)} yd.`
          },
          sorter: (a, b) => a?.radius - b?.radius
        },
        {
          title: 'WA Name',
          key: 'waName',
          width: '160px',
          scopedSlots: { customRender: 'wa-name' },
          sorter: (a, b) => {
            const nameA = a.work_area ? a.work_area.name : ''
            const nameB = b.work_area ? b.work_area.name : ''
            return nameA.localeCompare(nameB)
          }
        }
      ],

      outsideWorkAreaNote:
        'Actions with the label "Other" do not ' + 'result in the creation of "Outside Work Area" violations'
    }
  },
  computed: {
    menuItems() {
      return this.$store.state.applicationState.workAreas.map(item => ({
        key: item.id,
        icon: 'carbon:location-company-filled',
        label: item.name,
        visible: true,
        handler: record => {
          window.open(
            `https://www.google.com/maps/dir/?api=1&origin=${record.latitude},${record.longitude}&destination=${item.latitude},${item.longitude}&travelmode=driving`
          )
        }
      }))
    }
  },
  methods: {
    formatDateTime(record) {
      return Util.convertDateTimeToCurrentTimeZoneFormattedString(record.date)
    },
    formatDeviceType(record) {
      switch (record.device_type) {
        case 'tablet':
          return 'Tablet'
        case 'cell_phone':
          return 'Cell Phone'
        default:
          return record.device_type
      }
    },
    getTagColor(record) {
      return record.in_wa ? statusTagColors.GREEN : statusTagColors.RED
    },
    getWorkAreaName(record) {
      return record.work_area ? record.work_area.name : '-'
    },
    getActionLabel(record) {
      return shiftActionEnum[record.action] ?? 'Other'
    }
  }
}
</script>
